import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  TextField,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Semaphore } from './Semaphore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const schema = z.object({
  freeTrialNotesCount: z.coerce
    .number({
      required_error: 'Required (use 0 for disabling)',
      invalid_type_error: 'Must be a number',
    })
    .int()
    .min(0, { message: 'Must be >= 0' }),
});

export const TrialDurationUpdateForm = () => {
  const [updateConfig, { isLoading: isUpdatingTrialDuration }] =
    useUpdate('therapistAppConfig', { id: 'current' });

  const {
    remoteAppConfig: { freeTrialNotesCount },
  } = useGetAppConfig();

  const { onSettled } = useOnSettled();

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(schema as any),
    defaultValues: {
      freeTrialNotesCount,
    },
  });

  useEffect(() => {
    setValue('freeTrialNotesCount', freeTrialNotesCount);
  }, [freeTrialNotesCount, setValue]);

  const doSubmit = useCallback(
    async (data: any) => {
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data,
        },
        {
          onSettled,
        },
      );

      reset(data);
    },
    [updateConfig, onSettled, reset],
  );

  const onSubmit = handleSubmit(doSubmit);

  return (
    <Box marginTop="2rem">
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        marginBottom={1}
      >
        <Typography variant="h6">
          Free Trial <Semaphore enabled={!!freeTrialNotesCount} />
        </Typography>
        <Tooltip title="Number of free trial notes granted to new users. Set to 0 to disable free trials.">
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <TextField
            label="Granted Free Trial Notes"
            disabled={isUpdatingTrialDuration}
            error={!!formState.errors.freeTrialNotesCount}
            type="number"
            InputLabelProps={{
              shrink: true,
              sx: { paddingBottom: '2rem' },
            }}
            {...register('freeTrialNotesCount', { required: true })}
          />
          {(formState.isDirty || formState.isSubmitting) && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isUpdatingTrialDuration || !formState.isValid}
              endIcon={
                isUpdatingTrialDuration && (
                  <CircularProgress size={14} />
                )
              }
            >
              {isUpdatingTrialDuration ? 'Updating' : 'Update'} Trial
              Duration
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};
